import { createSlice } from "@reduxjs/toolkit";

/**
 * UI Slice - Global UI state management
 * 
 * This slice manages global UI states that can be controlled from anywhere in the app:
 * - GlobalLoader: A full-screen loading overlay that blocks user interaction
 * - Onboarding: Guided tour for new users
 * 
 * Usage:
 * - dispatch(showGlobalLoader()) - Shows the loader
 * - dispatch(showGlobalLoader("Loading message...")) - Shows loader with custom message
 * - dispatch(hideGlobalLoader()) - Hides the loader
 * - dispatch(startOnboarding()) - Starts the onboarding tour
 * - dispatch(completeOnboarding()) - Marks onboarding as complete
 * 
 * The loader should be used for:
 * - Critical operations that should block the UI (creating appointments, etc.)
 * - Operations where the user should wait for completion before proceeding
 */

// Onboarding step IDs
export const ONBOARDING_STEPS = {
  WELCOME: 'welcome',
  CREATE_PATIENT: 'create_patient',
  PATIENT_FORM: 'patient_form',
  CREATE_APPOINTMENT: 'create_appointment',
  APPOINTMENT_FORM: 'appointment_form',
  LIVE_SESSION_INTRO: 'live_session_intro',
  PENDING_ACTIONS: 'pending_actions',
  FINANCE: 'finance',
  TOOLBOX: 'toolbox',
  COMPLETE: 'complete',
};

const initialState = {
  // Global loader state
  globalLoader: {
    isVisible: false,
    message: null, // Optional loading message
  },
  
  // Onboarding state
  onboarding: {
    isActive: false,
    currentStep: ONBOARDING_STEPS.WELCOME,
    completedSteps: [],
    isCompleted: false,
    skipped: false,
    // Track data created during onboarding
    createdPatientId: null,
    createdAppointmentId: null,
  },
};

const uiSlice = createSlice({
  name: "ui",
  initialState,
  reducers: {
    /**
     * Shows the global loader overlay
     * @param {string} [payload] - Optional loading message to display
     */
    showGlobalLoader: (state, action) => {
      state.globalLoader.isVisible = true;
      state.globalLoader.message = action.payload || null;
    },
    
    /**
     * Hides the global loader overlay
     */
    hideGlobalLoader: (state) => {
      state.globalLoader.isVisible = false;
      state.globalLoader.message = null;
    },
    
    /**
     * Updates the loader message without changing visibility
     * @param {string} payload - New loading message
     */
    setGlobalLoaderMessage: (state, action) => {
      state.globalLoader.message = action.payload;
    },
    
    // ==========================================
    // ONBOARDING ACTIONS
    // ==========================================
    
    /**
     * Starts the onboarding tour
     */
    startOnboarding: (state) => {
      state.onboarding.isActive = true;
      state.onboarding.currentStep = ONBOARDING_STEPS.WELCOME;
      state.onboarding.completedSteps = [];
      state.onboarding.isCompleted = false;
      state.onboarding.skipped = false;
    },
    
    /**
     * Sets the current onboarding step
     * @param {string} payload - Step ID from ONBOARDING_STEPS
     */
    setOnboardingStep: (state, action) => {
      state.onboarding.currentStep = action.payload;
    },
    
    /**
     * Marks a step as completed and moves to the next
     * @param {string} payload - Step ID that was completed
     */
    completeOnboardingStep: (state, action) => {
      const stepId = action.payload;
      if (!state.onboarding.completedSteps.includes(stepId)) {
        state.onboarding.completedSteps.push(stepId);
      }
    },
    
    /**
     * Marks the entire onboarding as complete
     */
    completeOnboarding: (state) => {
      state.onboarding.isActive = false;
      state.onboarding.isCompleted = true;
    },
    
    /**
     * Skips the onboarding
     */
    skipOnboarding: (state) => {
      state.onboarding.isActive = false;
      state.onboarding.skipped = true;
    },
    
    /**
     * Sets the patient created during onboarding
     */
    setOnboardingPatientId: (state, action) => {
      state.onboarding.createdPatientId = action.payload;
    },
    
    /**
     * Sets the appointment created during onboarding
     */
    setOnboardingAppointmentId: (state, action) => {
      state.onboarding.createdAppointmentId = action.payload;
    },
    
    /**
     * Resets onboarding state (for replay)
     */
    resetOnboarding: (state) => {
      state.onboarding = initialState.onboarding;
    },
  },
});

// Export actions
export const { 
  showGlobalLoader, 
  hideGlobalLoader, 
  setGlobalLoaderMessage,
  // Onboarding actions
  startOnboarding,
  setOnboardingStep,
  completeOnboardingStep,
  completeOnboarding,
  skipOnboarding,
  setOnboardingPatientId,
  setOnboardingAppointmentId,
  resetOnboarding,
} = uiSlice.actions;

// Export selectors - Global Loader
export const selectGlobalLoaderVisible = (state) => state.ui.globalLoader.isVisible;
export const selectGlobalLoaderMessage = (state) => state.ui.globalLoader.message;
export const selectGlobalLoader = (state) => state.ui.globalLoader;

// Export selectors - Onboarding
export const selectOnboardingActive = (state) => state.ui.onboarding.isActive;
export const selectOnboardingStep = (state) => state.ui.onboarding.currentStep;
export const selectOnboardingCompleted = (state) => state.ui.onboarding.isCompleted;
export const selectOnboardingSkipped = (state) => state.ui.onboarding.skipped;
export const selectOnboardingCompletedSteps = (state) => state.ui.onboarding.completedSteps;
export const selectOnboardingCreatedPatientId = (state) => state.ui.onboarding.createdPatientId;
export const selectOnboardingCreatedAppointmentId = (state) => state.ui.onboarding.createdAppointmentId;
export const selectOnboarding = (state) => state.ui.onboarding;

export default uiSlice.reducer;
